import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { notification } from 'antd';
import type { DeepPartial } from 'react-hook-form';

import { ROUTES } from '@/components/Shared/routes/routes';
import { MODULE_CODES, WIDTH_FOR_OPENED_NAVBAR } from '@/constants';
import { personalApi } from '@/integrations/app.api/personal.api';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { getNavigate } from '@/services/helpers/navigate-helper';
import { orderAppMapper } from '@/services/mappers/orderAppMapper';
import { userInfoMapper } from '@/services/mappers/userInfoMapper';
import type { TMenuItem } from '@/types/app';

import { apiConnector } from '../integrations/api.connector';
import type { TUserInfoStore } from '../types/store';
import type {
  DecodedJWTToken,
  TModule,
  TUserResponse,
  TUserUpdateDto
} from '../types/user';
import { setIsAuth } from './auth.slice';
import { createAsyncThunk } from './create-async-thunk';
import { getRequestById } from './newOrder.slice';

export const getUserAccount = createAsyncThunk<TUserResponse, void>(
  'userInfo/getUserAccount',
  async () => {
    return apiConnector.getUserAccount();
  }
);

export const getUserMenu = createAsyncThunk(
  'userInfo/getUserMenu',
  async (dealerId: string, { dispatch }): Promise<TModule[]> => {
    const menu = await apiConnector.getUserMenu(dealerId);
    if (!menu || menu.length === 0) {
      const navigate = getNavigate();
      dispatch(setIsAuth(false));
      clearLocalStorageHelper();
      notification.error({
        title: 'Произошла ошибка! Попробуйте позже'
      });
      navigate(ROUTES.login);
      console.error('Меню не найдено или пустое');
      return [];
    }
    return menu;
  }
);

export const updateProfileUser = createAsyncThunk(
  'users/modules/personal-info',
  async (data: TUserUpdateDto) => personalApi.updateUserProfile(data)
);

const initialState: TUserInfoStore = {
  adminMobileMenu: '/adminPage/products',
  dealer: null,
  isLoading: false,
  isSidebarExpanded: window.innerWidth > WIDTH_FOR_OPENED_NAVBAR,
  jwtDecodedInfo: null,
  menu: [],
  faqMenu: [],
  mobileMenuItem: null,
  userAccesses: {
    autoCredit: {
      canAcceptAlternative: true,
      canAcceptOffer: true,
      canCancelOffer: true,
      canChangeChosenBankStatus: true,
      canChangeOfferStatus: true,
      canChangeStatusOnlineRequest: true,
      canChangeVehicleInfo: true,
      canConfirmInitialPayment: true,
      canReassignOnlineRequest: false,
      canRefundOffer: true,
      canResendRequest: true,
      canSendDocuments: true,
      canTakeToWorkOnlineRequest: false,
      canViewOnlineRequestHistory: false,
      canWriteCommentInRequest: false
    },
    global: {
      isAdmin: false
    },
    personal: {
      canViewOwnOnlineRequests: false
    },
    security: {
      hasGeneralAccessToSecurity: false
    },
    speechAnalytics: {
      hasAccessToSpeechAnalytics: false
    }
  },
  userInfo: null
};

export const userInfoSlice = createSlice({
  name: 'userInfoSlice',
  initialState,
  reducers: {
    setDealer: (
      state,
      action: PayloadAction<{ value: string; label: string; code: number }>
    ) => {
      state.dealer = action.payload;
    },
    setJwtDecodedInfo: (state, action: PayloadAction<DecodedJWTToken>) => {
      state.jwtDecodedInfo = action.payload;
    },
    setSidebarExpanded: (state, action: PayloadAction<boolean>) => {
      state.isSidebarExpanded = action.payload;
    },
    setMobileMenuItem: (state, action: PayloadAction<TModule>) => {
      state.mobileMenuItem = action.payload;
    },
    setAdminMobileMenu: (state, action) => {
      state.adminMobileMenu = action.payload;
    },
    setFaqMenu: (state, action: PayloadAction<TMenuItem[]>) => {
      state.faqMenu = action.payload;
    },
    updateUserAccesses: (
      state,
      action: PayloadAction<DeepPartial<TUserInfoStore['userAccesses']>>
    ) => {
      state.userAccesses = {
        autoCredit: {
          ...state.userAccesses.autoCredit,
          ...action.payload.autoCredit
        },
        global: {
          ...state.userAccesses.global,
          ...action.payload.global
        },
        personal: {
          ...state.userAccesses.personal,
          ...action.payload.personal
        },
        security: {
          ...state.userAccesses.security,
          ...action.payload.security
        },
        speechAnalytics: {
          ...state.userAccesses.speechAnalytics,
          ...action.payload.speechAnalytics
        }
      };
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(
        getUserAccount.fulfilled,
        (state, action: PayloadAction<TUserResponse>) => {
          const userInfo = userInfoMapper(action.payload);
          state.userInfo = userInfo;

          // Старший ФК
          const isSeniorFk = userInfo.position.code === 3;

          state.userAccesses.autoCredit.canReassignOnlineRequest ||= isSeniorFk;
          state.userAccesses.autoCredit.canViewOnlineRequestHistory ||=
            isSeniorFk;
        }
      )
      .addCase(getUserMenu.rejected, () => {
        const navigate = getNavigate();
        clearLocalStorageHelper();
        navigate(ROUTES.login);
        console.error('Меню не найдено или пустое');
      })
      .addCase(
        getUserMenu.fulfilled,
        (state, action: PayloadAction<TModule[]>) => {
          state.menu = action.payload
            ? action.payload.sort((a, b) => a.position - b.position)
            : [];
          const filteredMobileItems = state.menu.filter((item) =>
            ['pos-credit', 'leasing', 'clients', 'reports'].includes(item.code)
          );
          state.mobileMenuItem = filteredMobileItems[0];

          state.userAccesses.personal.canViewOwnOnlineRequests =
            !state.userAccesses.global.isAdmin &&
            action.payload.some(
              (item) => item.code === MODULE_CODES.autoCredit
            );
        }
      )
      .addCase(getRequestById.fulfilled, (state, action) => {
        const orderApp = orderAppMapper(action.payload);

        const { isAdmin } = state.userAccesses.global;
        const { isOnlineRequest, onlineClientRequest } = orderApp;
        const { takenByUser } = onlineClientRequest || {};

        const isTakenByCurrentUser =
          !!takenByUser && takenByUser.uuid === state.userInfo?.uuid;

        const canDoAnyAction =
          !isOnlineRequest || isAdmin || isTakenByCurrentUser;

        state.userAccesses = {
          ...state.userAccesses,
          autoCredit: {
            ...state.userAccesses.autoCredit,
            canAcceptAlternative: canDoAnyAction,
            canAcceptOffer: canDoAnyAction,
            canCancelOffer: canDoAnyAction,
            canChangeChosenBankStatus: canDoAnyAction,
            canChangeOfferStatus: canDoAnyAction,
            canChangeStatusOnlineRequest:
              isOnlineRequest && !isAdmin && isTakenByCurrentUser,
            canChangeVehicleInfo: canDoAnyAction,
            canConfirmInitialPayment: canDoAnyAction,
            canRefundOffer: canDoAnyAction,
            canResendRequest: canDoAnyAction,
            canSendDocuments: canDoAnyAction,
            canTakeToWorkOnlineRequest:
              isOnlineRequest &&
              !isAdmin &&
              !!onlineClientRequest &&
              !takenByUser,
            canWriteCommentInRequest: canDoAnyAction
          }
        };

        state.userAccesses.autoCredit.canReassignOnlineRequest ||=
          isOnlineRequest && isAdmin && !!takenByUser;
        state.userAccesses.autoCredit.canViewOnlineRequestHistory ||=
          isOnlineRequest && (isAdmin || isTakenByCurrentUser);
      });
  }
});

export const {
  setAdminMobileMenu,
  setDealer,
  setFaqMenu,
  setJwtDecodedInfo,
  setMobileMenuItem,
  setSidebarExpanded,
  updateUserAccesses
} = userInfoSlice.actions;
